import { useTranslation } from '../../i18n/useTranslation';
import React, { useState } from 'react';
import { 
  ShieldCheck, 
  Users, 
  Database, 
  AlertTriangle, 
  Sliders, 
  Terminal, 
  Activity, 
  ArrowLeft, 
  CheckCircle2, 
  Lock, 
  Zap, 
  RefreshCw, 
  Search, 
  FileText, 
  FileSpreadsheet,
  Power,
  Server,
  Layers,
  Check,
  Smartphone,
  Code,
  CreditCard,
  Scale,
  Award,
  Globe,
  LifeBuoy,
  Compass,
  Sparkles,
  TrendingUp,
  Target,
  DollarSign,
  Cpu,
  Settings
} from 'lucide-react';
import { AppView } from '../../types';
import { MOCK_ADMIN_USERS, MOCK_INCIDENTS, MOCK_FEATURE_FLAGS, MOCK_AUDIT_LOGS } from '../../services/admin/adminStore';
import { ControlCenterOverview } from './ControlCenterOverview';
import { SystemOverview } from './SystemOverview';

interface AdminSuperCommandCenterProps {
  onNavigateToView: (view: AppView) => void;
}

export const AdminSuperCommandCenter: React.FC<AdminSuperCommandCenterProps> = ({ onNavigateToView }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'data' | 'incidents' | 'flags' | 'audit' | 'health' | 'production' | 'mobile' | 'developer' | 'billing' | 'compliance' | 'quality' | 'globalization' | 'cxo' | 'cto' | 'cdo' | 'caio' | 'cpo' | 'cfo' | 'cro' | 'cro_risk'>('overview');
  
  // Local state for interactive admin actions
  const [flags, setFlags] = useState(MOCK_FEATURE_FLAGS);
  const [incidents, setIncidents] = useState(MOCK_INCIDENTS);
  const [auditLogs, setAuditLogs] = useState(MOCK_AUDIT_LOGS);
  const [killSwitchActive, setKillSwitchActive] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');

  const handleToggleFlag = (key: string) => {
    setFlags(prev => prev.map(f => {
      if (f.key === key) {
        const nextStatus = f.status === 'ON' ? 'OFF' : 'ON';
        // Add audit log
        setAuditLogs(logs => [
          {
            id: 'aud-' + Date.now(),
            timestamp: new Date().toISOString(),
            actor: 'admin.super@yamoopay.com',
            role: 'SuperAdmin',
            action: 'TOGGLE_FEATURE_FLAG',
            resource: f.key,
            status: 'SUCCESS',
            environment: 'PRODUCTION',
          },
          ...logs
        ]);
        return { ...f, status: nextStatus };
      }
      return f;
    }));
  };

  const handleResolveIncident = (id: string) => {
    setIncidents(prev => prev.map(inc => {
      if (inc.id === id) {
        return { ...inc, status: 'RESOLVED' as const };
      }
      return inc;
    }));
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] text-[#1A1A1A] pb-16">
      {/* Header */}
      <div className="bg-white border-b border-[#E5E7EB] px-6 py-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => onNavigateToView('homepage')}
              className="p-2 bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded-xl text-[#1A1A1A] transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded font-mono text-[10px] font-bold">PRODUCTION ENVIRONMENT</span>
                <span className="text-[11px] font-extrabold uppercase tracking-widest text-[#0E7044]">
                  YamooPay Super Admin Control Center (Prompt 16)
                </span>
              </div>
              <h1 className="text-2xl font-black tracking-tight text-[#1A1A1A]">
                Global Internal Operating System & SuperAdmin Shell
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setKillSwitchActive(!killSwitchActive)}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all shadow-xs flex items-center gap-2 cursor-pointer ${
                killSwitchActive 
                  ? 'bg-red-600 text-white animate-pulse' 
                  : 'bg-gray-100 hover:bg-gray-200 text-[#1A1A1A]'
              }`}
            >
              <Power className="w-4 h-4" />
              <span>{killSwitchActive ? 'MODE URGENCE (Kill Switch Actif)' : 'Kill Switches'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Sub-Tabs */}
      <div className="max-w-7xl mx-auto px-6 mt-6">
        <div className="flex border-b border-[#E5E7EB] gap-6 overflow-x-auto">
          {[
            { id: 'overview', label: 'Vue Exécutive', icon: Activity },
            { id: 'users', label: 'Utilisateurs & RBAC', icon: Users },
            { id: 'data', label: 'Données Financières', icon: Database },
            { id: 'incidents', label: `Incidents (${incidents.filter(i => i.status !== 'RESOLVED').length})`, icon: AlertTriangle },
            { id: 'flags', label: 'Feature Flags & Kill Switches', icon: Sliders },
            { id: 'audit', label: 'Journal d\'Audit', icon: Terminal },
            { id: 'health', label: 'Santé Système & Workers', icon: Server },
            { id: 'production', label: 'Production Engineering (DevOps)', icon: Server },
            { id: 'mobile', label: 'Mobile & PWA Command Center', icon: Smartphone },
            { id: 'developer', label: 'Developer Platform (API & Partners)', icon: Code },
            { id: 'billing', label: 'Billing & Monetization (P21)', icon: CreditCard },
            { id: 'compliance', label: 'Legal & Compliance CCO (P22)', icon: Scale },
            { id: 'quality', label: 'Quality & Release Assurance (P23)', icon: Award },
            { id: 'globalization', label: 'Global Expansion & I18n (P24)', icon: Globe },
            { id: 'cxo', label: 'Customer Experience CXO (P25)', icon: LifeBuoy },
            { id: 'cto', label: 'Technology & Architecture CTO (P26)', icon: Compass },
            { id: 'cdo', label: 'Data Governance & AI Readiness CDO (P27)', icon: Database },
            { id: 'caio', label: 'Chief AI Officer & LLMOps CAIO (P28)', icon: Sparkles },
            { id: 'cpo', label: 'Chief Product Officer CPO (P29)', icon: Target },
            { id: 'cfo', label: 'Chief Financial Officer CFO (P30)', icon: DollarSign },
            { id: 'cro', label: 'Chief Revenue Officer CRO (P31)', icon: TrendingUp },
            { id: 'cro_risk', label: 'Chief Risk Officer CRO - Risk (P32)', icon: AlertTriangle },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`pb-3 text-xs font-bold border-b-2 transition-colors flex items-center gap-2 shrink-0 cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-[#0E7044] text-[#0E7044]'
                    : 'border-transparent text-[#6B7280] hover:text-[#1A1A1A]'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Contents */}
        <div className="mt-8">
          
          {activeTab === 'production' && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-8 shadow-2xs text-center max-w-2xl mx-auto">
              <Server className="w-16 h-16 text-[#0E7044] mx-auto mb-6" />
              <h2 className="text-2xl font-black text-[#1A1A1A] mb-4">Production Engineering Command Center</h2>
              <p className="text-sm text-[#4B5563] mb-8 leading-relaxed">
                Accédez à l'environnement complet d'infrastructure de YamooPay: CI/CD, Déploiements, PostgreSQL Governance, Redis, OpenTelemetry, Stratégie FinOps et plans Disaster Recovery.
              </p>
              <button
                onClick={() => onNavigateToView('production-command-center')}
                className="px-6 py-3 bg-[#0E7044] hover:bg-[#0B5C37] text-white rounded-xl font-bold transition-colors cursor-pointer"
              >
                Ouvrir le Centre DevOps & SRE
              </button>
            </div>
          )}

          {activeTab === 'mobile' && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-8 shadow-2xs text-center max-w-2xl mx-auto">
              <Smartphone className="w-16 h-16 text-[#1E40AF] mx-auto mb-6" />
              <h2 className="text-2xl font-black text-[#1A1A1A] mb-4">Mobile & PWA Experience Command Center</h2>
              <p className="text-sm text-[#4B5563] mb-8 leading-relaxed">
                Supervise PWA architecture, Service Worker, deterministic offline execution, intelligent cache, sync outbox and Web Push context notifications.
              </p>
              <button
                onClick={() => onNavigateToView('mobile-command-center')}
                className="px-6 py-3 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-xl font-bold transition-colors cursor-pointer"
              >
                Ouvrir le Centre Mobile & PWA
              </button>
            </div>
          )}

          {activeTab === 'developer' && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-8 shadow-2xs text-center max-w-2xl mx-auto">
              <Code className="w-16 h-16 text-[#1E40AF] mx-auto mb-6" />
              <h2 className="text-2xl font-black text-[#1A1A1A] mb-4">Developer & Partner Ecosystem Platform</h2>
              <p className="text-sm text-[#4B5563] mb-8 leading-relaxed">
                Gérez les APIs publiques déterministes, les clés d'API hachées, les contrats OpenAPI 3.x, les webhooks signés HMAC-SHA256, les widgets intégrables et le registre de droits de redistribution des données financières.
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => onNavigateToView('developer-portal')}
                  className="px-6 py-3 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-xl font-bold transition-colors cursor-pointer"
                >
                  Ouvrir le Portail Développeur
                </button>
                <button
                  onClick={() => onNavigateToView('developer-command-center')}
                  className="px-6 py-3 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-xl font-bold transition-colors cursor-pointer"
                >
                  Command Center Développeur
                </button>
              </div>
            </div>
          )}

          {activeTab === 'billing' && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-8 shadow-2xs text-center max-w-2xl mx-auto">
              <CreditCard className="w-16 h-16 text-[#1E40AF] mx-auto mb-6" />
              <h2 className="text-2xl font-black text-[#1A1A1A] mb-4">Global Billing, Subscriptions & Monetization</h2>
              <p className="text-sm text-[#4B5563] mb-8 leading-relaxed">
                Supervise payment gateways (Stripe, Moneroo, Chariow), canonical subscriptions, API usage billing, VAT/tax rules, promo coupons and Monetization Master Switch.
              </p>
              <div className="flex justify-center gap-3 flex-wrap">
                <button
                  onClick={() => onNavigateToView('admin-monetization')}
                  className="px-6 py-3 bg-[#0F4F4F] hover:bg-[#0A3D3D] text-white rounded-xl font-bold transition-colors cursor-pointer flex items-center gap-2"
                >
                  <DollarSign className="w-4 h-4 text-emerald-400" />
                  <span>Monetization & Governance (Page 22)</span>
                </button>
                <button
                  onClick={() => onNavigateToView('billing-command-center')}
                  className="px-6 py-3 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-xl font-bold transition-colors cursor-pointer"
                >
                  Billing Command Center
                </button>
                <button
                  onClick={() => onNavigateToView('customer-billing-portal')}
                  className="px-6 py-3 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-xl font-bold transition-colors cursor-pointer"
                >
                  Customer Billing Portal
                </button>
              </div>
            </div>
          )}

          {activeTab === 'compliance' && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-8 shadow-2xs text-center max-w-2xl mx-auto">
              <Scale className="w-16 h-16 text-[#1E40AF] mx-auto mb-6" />
              <h2 className="text-2xl font-black text-[#1A1A1A] mb-4">Global Legal, Privacy & Compliance OS</h2>
              <p className="text-sm text-[#4B5563] mb-8 leading-relaxed">
                Supervise GDPR, UK GDPR, CCPA/CPRA compliance, RoPA register (Art. 30), DSAR requests, FinTech regulatory barriers, EU AI Act, WCAG 2.2 AA accessibility and Market Launch Gates.
              </p>
              <div className="flex justify-center gap-3 flex-wrap">
                <button
                  onClick={() => onNavigateToView('admin-compliance')}
                  className="px-6 py-3 bg-[#0F4F4F] hover:bg-[#0A3D3D] text-white rounded-xl font-bold transition-colors cursor-pointer flex items-center gap-2"
                >
                  <ShieldCheck className="w-4 h-4 text-emerald-400" />
                  <span>Compliance Monitor (Page 18)</span>
                </button>
                <button
                  onClick={() => onNavigateToView('admin-audit')}
                  className="px-6 py-3 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-xl font-bold transition-colors cursor-pointer flex items-center gap-2"
                >
                  <FileText className="w-4 h-4 text-emerald-400" />
                  <span>Audit Logs (Page 19)</span>
                </button>
                <button
                  onClick={() => onNavigateToView('admin-reports')}
                  className="px-6 py-3 bg-[#0F4F4F] hover:bg-[#0A3D3D] text-white rounded-xl font-bold transition-colors cursor-pointer flex items-center gap-2"
                >
                  <FileSpreadsheet className="w-4 h-4 text-emerald-400" />
                  <span>Audit & Reports (Page 20)</span>
                </button>
                <button
                  onClick={() => onNavigateToView('admin-configuration')}
                  className="px-6 py-3 bg-[#0F4F4F] hover:bg-[#0A3D3D] text-white rounded-xl font-bold transition-colors cursor-pointer flex items-center gap-2"
                >
                  <Settings className="w-4 h-4 text-emerald-400" />
                  <span>Configuration (Page 21)</span>
                </button>
                <button
                  onClick={() => onNavigateToView('compliance-command-center')}
                  className="px-6 py-3 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-xl font-bold transition-colors cursor-pointer"
                >
                  CCO Command Center
                </button>
                <button
                  onClick={() => onNavigateToView('legal-portal')}
                  className="px-6 py-3 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-xl font-bold transition-colors cursor-pointer"
                >
                  Portail Légal Public
                </button>
              </div>
            </div>
          )}

          {activeTab === 'quality' && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-8 shadow-2xs text-center max-w-2xl mx-auto">
              <Award className="w-16 h-16 text-blue-600 mx-auto mb-6" />
              <h2 className="text-2xl font-black text-[#1A1A1A] mb-4">Chief Quality Officer & Release Assurance OS</h2>
              <p className="text-sm text-[#4B5563] mb-8 leading-relaxed">
                Supervise mathematical validation of financial engines, 44 golden test cases with independent provenance (IRS, HMRC, CRA, DGFiP), generative property tests, API contracts, WCAG 2.2 AA accessibility and immutable release certificates.
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => onNavigateToView('cqo-command-center')}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-colors cursor-pointer"
                >
                  Ouvrir CQO Command Center
                </button>
              </div>
            </div>
          )}

          {activeTab === 'globalization' && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-8 shadow-2xs text-center max-w-2xl mx-auto">
              <Globe className="w-16 h-16 text-blue-600 mx-auto mb-6" />
              <h2 className="text-2xl font-black text-[#1A1A1A] mb-4">Chief Global Expansion Officer OS</h2>
              <p className="text-sm text-[#4B5563] mb-8 leading-relaxed">
                Supervise YamooPay global expansion, Country & Market Packs, BCP 47/CLDR registry, Translation Management System (TMS), financial concepts glossary and strict jurisdictional isolation rules.
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => onNavigateToView('global-expansion-command-center')}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-colors cursor-pointer"
                >
                  Ouvrir Global Expansion Command Center
                </button>
              </div>
            </div>
          )}

          {activeTab === 'cxo' && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-8 shadow-2xs text-center max-w-2xl mx-auto">
              <LifeBuoy className="w-16 h-16 text-indigo-600 mx-auto mb-6" />
              <h2 className="text-2xl font-black text-[#1A1A1A] mb-4">Chief Customer Experience Officer (CXO) OS</h2>
              <p className="text-sm text-[#4B5563] mb-8 leading-relaxed">
                Supervise global customer support, Help Center & Knowledge Base, financial mathematical escalations (Salary/Mortgage/FX), Developer API support, B2B billing support, AI Copilot and Voice of Customer (CSAT/NPS).
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => onNavigateToView('cxo-command-center')}
                  className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition-colors cursor-pointer"
                >
                  Ouvrir CXO Command Center
                </button>
                <button
                  onClick={() => onNavigateToView('help-center')}
                  className="px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-xl font-bold transition-colors cursor-pointer"
                >
                  Consulter le Help Center Public
                </button>
              </div>
            </div>
          )}

          {activeTab === 'cto' && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-8 shadow-2xs text-center max-w-2xl mx-auto">
              <Compass className="w-16 h-16 text-cyan-600 mx-auto mb-6" />
              <h2 className="text-2xl font-black text-[#1A1A1A] mb-4">Chief Technology Officer (CTO) & Architecture OS</h2>
              <p className="text-sm text-[#4B5563] mb-8 leading-relaxed">
                Supervise global technology governance, Developer Portal (IDP), technical debt, microservices matrix, Zero-Trust security & Vault secret rotation, SOC2/ISO27001 compliance and Cloud FinOps.
              </p>
              <div className="flex justify-center gap-3 flex-wrap">
                <button
                  onClick={() => onNavigateToView('cto-command-center')}
                  className="px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl font-bold transition-colors cursor-pointer"
                >
                  Ouvrir CTO Command Center
                </button>
                <button
                  onClick={() => onNavigateToView('internal-developer-platform')}
                  className="px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold transition-colors cursor-pointer"
                >
                  Accéder à l&apos;Internal Developer Platform
                </button>
              </div>
            </div>
          )}

          {activeTab === 'cdo' && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-8 shadow-2xs text-center max-w-2xl mx-auto">
              <Database className="w-16 h-16 text-indigo-600 mx-auto mb-6" />
              <h2 className="text-2xl font-black text-[#1A1A1A] mb-4">Chief Data Officer (CDO) & AI Data Readiness OS</h2>
              <p className="text-sm text-[#4B5563] mb-8 leading-relaxed">
                Pilotez la gouvernance des données 2.0 de YamooPay : Data Catalog, Data Products certifiés, Master Data Management (SoR), Contrats de données, Métriques gouvernées (Semantic Layer), Rétention RGPD et matrice AI Data Readiness (RAG & LLM eligibility).
              </p>
              <div className="flex justify-center gap-3 flex-wrap">
                <button
                  onClick={() => onNavigateToView('cdo-command-center')}
                  className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition-colors cursor-pointer"
                >
                  Ouvrir CDO Command Center
                </button>
                <button
                  onClick={() => onNavigateToView('enterprise-data-catalog')}
                  className="px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold transition-colors cursor-pointer"
                >
                  Consulter le Data Catalog
                </button>
              </div>
            </div>
          )}

          {activeTab === 'caio' && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-8 shadow-2xs text-center max-w-2xl mx-auto">
              <Sparkles className="w-16 h-16 text-purple-600 mx-auto mb-6" />
              <h2 className="text-2xl font-black text-[#1A1A1A] mb-4">Chief AI Officer (CAIO) & LLMOps OS</h2>
              <p className="text-sm text-[#4B5563] mb-8 leading-relaxed">
                Supervise YamooPay AI Platform: Model Gateway, multi-model dynamic routing (Gemini 3.5 Pro/Flash, Fallbacks), versioned Prompt Registry, RAGOps & pgvector, bounded Agents & Tool Governance, Guardrails, AI Evals & Release Certification, AI FinOps & Kill Switches.
              </p>
              <div className="flex justify-center gap-3 flex-wrap">
                <button
                  onClick={() => onNavigateToView('admin-ai-platform')}
                  className="px-6 py-3 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-xl font-bold transition-colors cursor-pointer flex items-center gap-2"
                >
                  <Cpu className="w-4 h-4 text-emerald-400" />
                  <span>AI Platform & Model Routing (Page 17)</span>
                </button>
                <button
                  onClick={() => onNavigateToView('caio-command-center')}
                  className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-bold transition-colors cursor-pointer"
                >
                  Ouvrir CAIO Command Center
                </button>
                <button
                  onClick={() => onNavigateToView('ai-sandbox-playground')}
                  className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-xl font-bold transition-colors cursor-pointer"
                >
                  Tester dans l&apos;AI Sandbox
                </button>
              </div>
            </div>
          )}

          {activeTab === 'cpo' && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-8 shadow-2xs text-center max-w-2xl mx-auto">
              <Target className="w-16 h-16 text-blue-600 mx-auto mb-6" />
              <h2 className="text-2xl font-black text-[#1A1A1A] mb-4">Chief Product Officer (CPO) & Product Operating System</h2>
              <p className="text-sm text-[#4B5563] mb-8 leading-relaxed">
                Pilotez le Product Operating System de YamooPay (Prompt 29) : Stratégie & Non-Goals, Portfolio Management, Customer Discovery, Problèmes & Paris Produit (Product Bets), PRDs & Traçabilité Exigences, Gouvernance d&apos;Expérimentation & Feature Flags, Product-Led Growth (PLG), Pricing Research, OKRs & Audit Log des Décisions.
              </p>
              <div className="flex justify-center gap-3 flex-wrap">
                <button
                  onClick={() => onNavigateToView('cpo-command-center')}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-colors cursor-pointer"
                >
                  Ouvrir CPO Command Center
                </button>
              </div>
            </div>
          )}

          {activeTab === 'cfo' && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-8 shadow-2xs text-center max-w-2xl mx-auto">
              <DollarSign className="w-16 h-16 text-emerald-600 mx-auto mb-6" />
              <h2 className="text-2xl font-black text-[#1A1A1A] mb-4">Chief Financial Officer (CFO) & Financial Control Tower</h2>
              <p className="text-sm text-[#4B5563] mb-8 leading-relaxed">
                Pilotez la plateforme Finance & FP&A de YamooPay (Prompt 30) : Traçabilité des revenus certifiés AdSense & Affiliation, FP&A & centres de coûts, modélisation de scénarios avec Master Switch Paid Monetization, Unit Economics (CAC, LTV, Payback), Trésorerie multi-devises à 13 semaines, Contrôles financiers & Dossier Board Pack (Q3 2026).
              </p>
              <div className="flex justify-center gap-3 flex-wrap">
                <button
                  onClick={() => onNavigateToView('cfo-command-center')}
                  className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold transition-colors cursor-pointer"
                >
                  Ouvrir CFO Command Center
                </button>
              </div>
            </div>
          )}

          {activeTab === 'cro' && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-8 shadow-2xs text-center max-w-2xl mx-auto">
              <TrendingUp className="w-16 h-16 text-[#0E7044] mx-auto mb-6" />
              <h2 className="text-2xl font-black text-[#1A1A1A] mb-4">Chief Revenue Officer (CRO) & Global GTM OS</h2>
              <p className="text-sm text-[#4B5563] mb-8 leading-relaxed">
                Pilotez le système d&apos;exploitation commercial global de YamooPay (Prompt 31) : Gestion des Leads multi-signaux, Pipeline d&apos;Opportunités et étapes de qualification, Devis governés & Deal Desk exceptions, Sales Forecast Engine multi-devises, Partenariats stratégiques, Expansion clients & Renewals.
              </p>
              <div className="flex justify-center gap-3 flex-wrap">
                <button
                  onClick={() => onNavigateToView('cro-revenue-command-center')}
                  className="px-6 py-3 bg-[#0E7044] hover:bg-[#0B5C37] text-white rounded-xl font-bold transition-colors cursor-pointer"
                >
                  Ouvrir CRO Revenue Command Center
                </button>
              </div>
            </div>
          )}

          {activeTab === 'cro_risk' && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-8 shadow-2xs text-center max-w-2xl mx-auto">
              <AlertTriangle className="w-16 h-16 text-[#0E7044] mx-auto mb-6" />
              <h2 className="text-2xl font-black text-[#1A1A1A] mb-4">Chief Risk Officer (CRO) & Enterprise Risk Operating System</h2>
              <p className="text-sm text-[#4B5563] mb-8 leading-relaxed">
                Pilotez le système d&apos;exploitation des risques de YamooPay (Prompt 32) : Registre des Risques Entreprise (ISO 31000), Déclarations d&apos;Appétit & KRIs, Laboratoire de Tests de Contrôles, Résilience Opérationnelle & Plans de Continuité (BCP ISO 22301), Gouvernance des Enquêtes de Fraude, Modèle à Trois Lignes (IIA 2026), Transfert de Risques Assurances & IA Risk Copilot.
              </p>
              <div className="flex justify-center gap-3 flex-wrap">
                <button
                  onClick={() => onNavigateToView('cro-risk-command-center')}
                  className="px-6 py-3 bg-[#0E7044] hover:bg-[#0B5C37] text-white rounded-xl font-bold transition-colors cursor-pointer"
                >
                  Ouvrir Chief Risk Officer Command Center
                </button>
              </div>
            </div>
          )}

          {activeTab === 'overview' && (
            <ControlCenterOverview onNavigateToView={onNavigateToView} />
          )}

          {activeTab === 'users' && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-2xs space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-[#1A1A1A]">Gestion des Administrateurs & RBAC</h3>
                  <p className="text-xs text-[#6B7280] mt-0.5">Contrôle des accès basés sur les rôles et permissions serveurs</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => onNavigateToView('admin-roles-permissions')}
                    className="px-3 py-1.5 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-xl text-xs font-bold transition-colors cursor-pointer flex items-center gap-1.5"
                  >
                    <Sliders className="w-3.5 h-3.5" />
                    <span>Configuration des Rôles & Permissions</span>
                  </button>
                  <span className="px-3 py-1 bg-emerald-50 text-[#0E7044] rounded-full text-xs font-bold">
                    {MOCK_ADMIN_USERS.length} Admins Actifs
                  </span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-[#E5E7EB] text-[#6B7280] font-bold">
                      <th className="pb-3">Nom</th>
                      <th className="pb-3">Email</th>
                      <th className="pb-3">Rôle RBAC</th>
                      <th className="pb-3">MFA</th>
                      <th className="pb-3">Dernière Connexion</th>
                      <th className="pb-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E7EB]">
                    {MOCK_ADMIN_USERS.map((usr) => (
                      <tr key={usr.id} className="text-[#1A1A1A]">
                        <td className="py-3 font-bold">{usr.name}</td>
                        <td className="py-3 font-mono text-[#6B7280]">{usr.email}</td>
                        <td className="py-3 font-mono text-[#0E7044] font-bold">{usr.role}</td>
                        <td className="py-3">
                          <span className="px-2 py-0.5 bg-emerald-100 text-[#0E7044] rounded text-[10px] font-bold">Enforced</span>
                        </td>
                        <td className="py-3 font-mono text-[#6B7280]">{new Date(usr.lastLogin).toLocaleTimeString()}</td>
                        <td className="py-3 text-right">
                          <button className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-[#1A1A1A] rounded-lg text-xs font-bold transition-colors cursor-pointer">
                            Auditer Sessions
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'data' && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-2xs space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-[#1A1A1A]">Opérations sur les Données Financières & Sources</h3>
                  <p className="text-xs text-[#6B7280] mt-0.5">Gestion complète des flux de données et connecteurs</p>
                </div>
                <button
                  onClick={() => onNavigateToView('data-source-management')}
                  className="px-4 py-2 bg-[#0E7044] hover:bg-[#0B5C37] text-white rounded-xl font-bold text-xs transition-colors cursor-pointer flex items-center gap-2"
                >
                  <Database className="w-4 h-4" />
                  <span>Ouvrir le Système de Gestion des Sources (DSMS)</span>
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl">
                  <div className="font-bold text-sm text-[#1A1A1A]">Taux de Change (FX Engine)</div>
                  <div className="text-xs text-[#0E7044] font-semibold mt-1">Source: ECB & Institutional Feed</div>
                  <div className="text-xs text-[#6B7280] mt-2">Dernière mise à jour réussie : Il y a 5 min.</div>
                </div>
                <div className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl">
                  <div className="font-bold text-sm text-[#1A1A1A]">Barèmes Fiscaux (Salary Engine)</div>
                  <div className="text-xs text-[#0E7044] font-semibold mt-1">Règles Actives : US, FR, CA, UK (2026)</div>
                  <div className="text-xs text-[#6B7280] mt-2">Validation par Data Steward : OK.</div>
                </div>
                <div className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl">
                  <div className="font-bold text-sm text-[#1A1A1A]">Fournisseurs de Transfert</div>
                  <div className="text-xs text-[#0E7044] font-semibold mt-1">12 Partenaires Actifs</div>
                  <div className="text-xs text-[#6B7280] mt-2">Disponibilité API : 99.98%.</div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'incidents' && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-2xs space-y-4">
              <h3 className="text-lg font-bold text-[#1A1A1A]">Centre de Commandement des Incidents</h3>
              <div className="space-y-3">
                {incidents.map((inc) => (
                  <div key={inc.id} className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-xs text-red-600">{inc.id}</span>
                        <span className="font-bold text-sm text-[#1A1A1A]">{inc.title}</span>
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-[10px] font-bold">{inc.severity}</span>
                      </div>
                      <div className="text-xs text-[#6B7280] mt-1">
                        Commandant : <strong>{inc.commander}</strong> | Début : {new Date(inc.startedAt).toLocaleTimeString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        inc.status === 'RESOLVED' ? 'bg-emerald-100 text-[#0E7044]' : 'bg-amber-100 text-amber-800'
                      }`}>
                        {inc.status}
                      </span>
                      {inc.status !== 'RESOLVED' && (
                        <button
                          onClick={() => handleResolveIncident(inc.id)}
                          className="px-3 py-1 bg-[#0E7044] hover:bg-[#0b5c37] text-white rounded-lg text-xs font-bold transition-colors cursor-pointer"
                        >
                          Résoudre
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'flags' && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-2xs space-y-4">
              <h3 className="text-lg font-bold text-[#1A1A1A]">Gestion des Feature Flags & Kill Switches</h3>
              <div className="space-y-3">
                {flags.map((f) => (
                  <div key={f.key} className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-sm text-[#1A1A1A]">{f.name}</span>
                        <span className="px-2 py-0.5 bg-gray-200 text-[#4B5563] rounded font-mono text-[10px]">{f.key}</span>
                      </div>
                      <div className="text-xs text-[#6B7280] mt-1">{f.description}</div>
                      <div className="text-[11px] text-[#9CA3AF] mt-1">Propriétaire : {f.owner}</div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        f.status === 'ON' ? 'bg-emerald-100 text-[#0E7044]' : 'bg-gray-200 text-gray-700'
                      }`}>
                        {f.status}
                      </span>
                      <button
                        onClick={() => handleToggleFlag(f.key)}
                        className="px-3 py-1.5 bg-[#1A1A1A] hover:bg-black text-white rounded-xl text-xs font-bold transition-colors cursor-pointer"
                      >
                        Basculer
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'audit' && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-2xs space-y-4">
              <h3 className="text-lg font-bold text-[#1A1A1A]">Journal d'Audit Immuable (Audit Center)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-[#E5E7EB] text-[#6B7280] font-bold">
                      <th className="pb-3">Horodatage</th>
                      <th className="pb-3">Acteur</th>
                      <th className="pb-3">Rôle</th>
                      <th className="pb-3">Action</th>
                      <th className="pb-3">Ressource</th>
                      <th className="pb-3 text-right">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E7EB]">
                    {auditLogs.map((log) => (
                      <tr key={log.id} className="text-[#1A1A1A]">
                        <td className="py-3 font-mono text-[#6B7280]">{new Date(log.timestamp).toLocaleTimeString()}</td>
                        <td className="py-3 font-bold">{log.actor}</td>
                        <td className="py-3 font-mono text-[#0E7044]">{log.role}</td>
                        <td className="py-3 font-mono font-bold">{log.action}</td>
                        <td className="py-3 font-mono text-[#6B7280]">{log.resource}</td>
                        <td className="py-3 text-right">
                          <span className="px-2 py-0.5 bg-emerald-100 text-[#0E7044] rounded text-[10px] font-bold">{log.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'health' && (
            <SystemOverview onNavigateToView={onNavigateToView} />
          )}
        </div>
      </div>
    </div>
  );
};
